import React, { useCallback, useState } from "react";
import ContentList from "./ContentList";
import SuggestedConnections from "./SuggestedConnections";
import CreatePost from "../create_page/CreatePost";
import SignedOutPrompt from "../SignedOutPrompt";
import { isAccountVerified, defaultPfpUrl } from "../utils";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";

/**
 * Teaser that opens the create-post composer.
 * @param {Object} props
 * @returns {JSX.Element}
 */
function PostComposerTeaser({ user, onOpen }) {
  const displayName =
    user?.name ||
    (user?.first_name || user?.last_name
      ? `${user.first_name || ""} ${user.last_name || ""}`.trim()
      : user?.username) ||
    "you";

  return (
    <button
      type="button"
      onClick={onOpen}
      className="mb-6 flex w-full items-center gap-3 rounded-lg border border-ui-border bg-ui-card p-4 text-left shadow-shell-sm transition hover:bg-highlight focus:outline-none focus-visible:ring-2 focus-visible:ring-accent"
    >
      <img
        src={user?.pfp_url || defaultPfpUrl}
        alt=""
        className="h-11 w-11 shrink-0 rounded-full object-cover"
        onError={(e) => {
          e.currentTarget.src = defaultPfpUrl;
        }}
      />
      <div className="min-w-0 flex-1">
        <div className="truncate text-sm font-semibold text-text-primary">
          Share something, {displayName}
        </div>
        <div className="text-xs text-text-muted">Post, prayer, scripture, or sermon</div>
      </div>
      <span className="shrink-0 rounded-md bg-accent px-3 py-1.5 text-sm font-medium text-text-on-accent">
        Create
      </span>
    </button>
  );
}

/**
 * Logged-in home feed with composer modal and suggestions rail.
 * Scrolls in the shell main below the header.
 * Organizations do not get the suggestions rail (they cannot connect with users).
 *
 * @param {Object} props
 * @param {number} props.userId
 * @param {object} props.user
 * @returns {JSX.Element}
 */
const FeedPage = ({ userId, user }) => {
  const [composerOpen, setComposerOpen] = useState(false);
  const [feedRefreshKey, setFeedRefreshKey] = useState(0);
  const isOrganization = user?.type === "organization";

  const handlePostCreated = useCallback(() => {
    setComposerOpen(false);
    setFeedRefreshKey((k) => k + 1);
  }, []);

  if (!userId) {
    return (
      <SignedOutPrompt
        title="Your feed"
        description="Sign in to see posts and events from people and ministries in your network."
      />
    );
  }

  if (user && !isAccountVerified(user)) {
    return (
      <SignedOutPrompt
        variant="restricted"
        title="Your feed"
        description="Your account must be verified before you can view the feed. Until then, you can browse Explore and manage your profile."
      />
    );
  }

  return (
    <div className="bg-ui-background pb-8">
      <div
        className={
          isOrganization
            ? "mx-auto max-w-3xl px-3 py-6 sm:px-4"
            : "mx-auto grid max-w-5xl items-start gap-6 px-3 py-6 sm:px-4 lg:grid-cols-[minmax(0,1fr)_280px]"
        }
      >
        <div className="min-w-0 overflow-x-hidden">
          {userId ? (
            <PostComposerTeaser user={user} onOpen={() => setComposerOpen(true)} />
          ) : null}
          <ContentList userId={userId} user={user} refreshKey={feedRefreshKey} />
        </div>
        {!isOrganization ? <SuggestedConnections userId={userId} user={user} /> : null}
      </div>

      <Dialog open={composerOpen} onOpenChange={setComposerOpen}>
        <DialogContent
          className="max-h-[90vh] max-w-xl overflow-y-auto p-0 sm:rounded-xl"
          onOpenAutoFocus={(event) => {
            event.preventDefault();
            const root = event.currentTarget instanceof Element ? event.currentTarget : null;
            const target =
              root?.querySelector("[data-composer-autofocus]") ||
              document.querySelector("#create-post-caption[data-composer-autofocus], #create-post-location[data-composer-autofocus]");
            if (target instanceof HTMLElement) {
              target.focus({ preventScroll: true });
            }
          }}
        >
          <DialogHeader className="sr-only">
            <DialogTitle>Create a post</DialogTitle>
          </DialogHeader>
          <CreatePost
            userId={userId}
            user={user}
            variant="composer"
            onClose={() => setComposerOpen(false)}
            onPostCreated={handlePostCreated}
          />
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default FeedPage;
